import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getLocale, getString, ILocaleStrings } from '../../../locale'
import Loader from '../../../components/loader'
import { getPublicTexts, setPublicText, PublicTextEntry } from '../../../api/0x0005/general-settings'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    entries: PublicTextEntry[]
    editingKeyId?: number
    editValue: string
}

const KEY_LABELS: { [key: number]: keyof ILocaleStrings } = {
    1: 'public_texts.key_1',
    2: 'public_texts.key_2',
    3: 'public_texts.key_3',
    4: 'public_texts.key_4',
    5: 'public_texts.key_5',
    6: 'public_texts.key_6',
    7: 'public_texts.key_7',
    8: 'public_texts.key_8',
    9: 'public_texts.key_9',
    10: 'public_texts.key_10',
    11: 'public_texts.key_11',
    12: 'public_texts.key_12',
    13: 'public_texts.key_13',
    14: 'public_texts.key_14',
    15: 'public_texts.key_15',
    16: 'public_texts.key_16',
}

export default class PublicTextsTab extends Component<Props, State> {
    state: State = {
        loading: true,
        entries: [],
        editValue: '',
    }

    componentDidMount() {
        this.load()
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            const data = await getPublicTexts(this.props.clientId)
            this.setState({ entries: data.entries, loading: false })
        } catch (error: any) {
            toast.error(error.message || getString('public_texts.load_error'))
            this.setState({ loading: false })
        }
    }

    currentValue = (entry: PublicTextEntry) => (getLocale() === 'he' ? entry.heOverride : entry.enOverride)

    currentLanguage = () => (getLocale() === 'he' ? 1 : 0)

    openEdit = (entry: PublicTextEntry) => {
        this.setState({
            editingKeyId: entry.keyId,
            editValue: this.currentValue(entry),
        })
    }

    closeEdit = () => {
        this.setState({ editingKeyId: undefined, editValue: '' })
    }

    save = async () => {
        const { editingKeyId, editValue } = this.state
        if (!editingKeyId) return
        try {
            await setPublicText(this.props.clientId, editingKeyId, this.currentLanguage(), editValue)
            toast.success(getString('public_texts.save_success'))
            await this.load()
            this.closeEdit()
        } catch (error: any) {
            toast.error(error.message || getString('public_texts.save_error'))
        }
    }

    reset = async (keyId: number) => {
        try {
            await setPublicText(this.props.clientId, keyId, this.currentLanguage(), '')
            toast.success(getString('public_texts.reset_success'))
            await this.load()
        } catch (error: any) {
            toast.error(error.message || getString('public_texts.save_error'))
        }
    }

    renderEditModal() {
        const { editingKeyId, editValue } = this.state
        if (!editingKeyId) return null
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{getString(KEY_LABELS[editingKeyId])}</h2>
                    <textarea
                        value={editValue}
                        onChange={e => this.setState({ editValue: e.target.value })}
                        maxLength={240}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    />
                    <div className="text-xs text-gray-500 mt-2">{editValue.length}/240</div>
                    <div className="flex gap-3 mt-4">
                        <button onClick={this.closeEdit} className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 rounded">
                            {getString('common.cancel')}
                        </button>
                        <button onClick={this.save} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded">
                            {getString('common.save')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    render() {
        if (this.state.loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{getString('public_texts.title')}</h1>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{getString('public_texts.description')}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {this.state.entries.map(entry => (
                        <div key={entry.keyId} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                            <h3 className="font-semibold text-gray-800 dark:text-white">{getString(KEY_LABELS[entry.keyId])}</h3>
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 min-h-[40px]">
                                {this.currentValue(entry) || getString('public_texts.default_mode')}
                            </p>
                            <div className="mt-4 flex gap-2">
                                <button onClick={() => this.openEdit(entry)} className="px-3 py-1 bg-blue-600 text-white rounded">
                                    {getString('public_texts.edit')}
                                </button>
                                <button onClick={() => this.reset(entry.keyId)} className="px-3 py-1 bg-gray-500 text-white rounded">
                                    {getString('public_texts.reset')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                {this.renderEditModal()}
            </div>
        )
    }
}
